/**
 * The pieleaf server client.
 *
 * Two surfaces, deliberately:
 *   - `storefrontFetch` hits the public REST routes and is used for guest
 *     checkout and guest tracking. Those must work with no session at all.
 *   - `publicGraphql` reads the catalogue, which carries `USING (true)` SELECT
 *     policies and needs no session.
 *   - `graphqlWith` takes a session-bound fetch (see `usePieleafGraphql`) for the
 *     buyer surfaces, where RLS decides what the caller may see.
 *
 * The server owns every price. Nothing here computes a total.
 */

import { MissingEnvError } from '@pieleaf/lib/missing-env-error';

function apiBaseUrl(): string {
  const url = process.env.NEXT_PUBLIC_PIELEAF_API_URL;
  if (!url) throw new MissingEnvError('NEXT_PUBLIC_PIELEAF_API_URL');
  return url.replace(/\/$/, '');
}

export type ApiFailure = { code: number; message: string };

export class PieleafApiError extends Error {
  readonly code: number;

  constructor(failure: ApiFailure) {
    super(failure.message);
    this.name = 'PieleafApiError';
    this.code = failure.code;
  }
}

async function readError(response: Response): Promise<never> {
  const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
  throw new PieleafApiError({ code: response.status, message: body?.error?.message ?? response.statusText });
}

/** Public storefront REST — no session required. */
export async function storefrontFetch<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${apiBaseUrl()}${path}`, {
    body: JSON.stringify(body),
    headers: { 'content-type': 'application/json' },
    method: 'POST',
  });
  if (!response.ok) return readError(response);
  return (await response.json()) as T;
}

/**
 * Guarded GraphQL. The caller supplies the fetch so the token binds to the live
 * session — `useAuthFetch()` in a client component. A module-level fetch cannot
 * see the session and would silently send unauthenticated requests.
 */
export type ApiFetch = (input: string, init?: RequestInit) => Promise<Response>;

export async function graphqlWith<T>(fetchImpl: ApiFetch, query: string, variables?: Record<string, unknown>): Promise<T> {
  const response = await fetchImpl(`${apiBaseUrl()}/graphql`, {
    body: JSON.stringify({ query, variables }),
    headers: { 'content-type': 'application/json' },
    method: 'POST',
  });
  if (!response.ok) return readError(response);
  const payload = (await response.json()) as { data?: T; errors?: { message: string }[] };
  if (payload.errors?.length) {
    throw new PieleafApiError({ code: 400, message: payload.errors[0].message });
  }
  if (!payload.data) {
    throw new PieleafApiError({ code: 500, message: 'the server returned no data' });
  }
  return payload.data;
}

/** Catalogue reads — no session. */
export function publicGraphql<T>(query: string, variables?: Record<string, unknown>): Promise<T> {
  return graphqlWith<T>(fetch, query, variables);
}

export type PlacedOrder = { id: string; total_sum: string };

export type OrderTracking = {
  id: string;
  status: string;
  total_sum: string;
  product: Record<string, unknown>;
  delivery: Record<string, unknown>;
  payment_status: string | null;
  created_on: string | null;
  claimed: boolean;
};

export type PlaceOrderInput = {
  productId: number;
  quantity: number;
  townId: number;
  agency: string;
  contactName: string;
  contactPhone: string;
  processor: string;
  payerHint?: string;
};

/** Places an order. Returns the SERVER's total, which is the authoritative one. */
export function placeOrder(input: PlaceOrderInput): Promise<PlacedOrder> {
  return storefrontFetch<PlacedOrder>('/v1/orders', {
    agency: input.agency,
    contact_name: input.contactName,
    contact_phone: input.contactPhone,
    payer_hint: input.payerHint,
    processor: input.processor,
    product_id: input.productId,
    quantity: input.quantity,
    town_id: input.townId,
  });
}

/**
 * Tracks an order. The phone is REQUIRED — order ids are sequential, so a
 * lookup by id alone would let anyone enumerate other buyers' orders.
 */
export function trackOrder(id: number, phone: string): Promise<OrderTracking> {
  return storefrontFetch<OrderTracking>('/v1/orders/track', { id, phone });
}
